import classNames from 'classnames';
import type { CSSProperties } from 'react';

import { css } from '@sorare/css';

import { AnimatedNumber } from '#src/atoms/animations/AnimatedNumber/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { GemIcon } from '#src/atoms/icons/GemIcon.tsx';
import { Plus } from '#src/atoms/icons/Plus.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { DialogKey } from '#src/components/navigation/WithDialogs/index.tsx';
import { useOpenDialog } from '#src/hooks/navigation/useOpenDialog.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { OpenBuyGemsDialog_Context } from '#src/protos/events/so5/shared/events.ts';

const root = css`
  backdrop-filter: blur(64px);
  border-radius: var(--unit);
  padding: var(--half-unit) var(--intermediate-unit);
  background: var(--c-surface-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);

  &:hover,
  &:focus {
    background: var(--c-nd-200);
  }
  & svg {
    width: var(--unit);
    height: var(--unit);
  }

  &.vertical {
    padding: var(--half-unit);
    gap: 0;
    background: transparent;
  }
`;

const plusIcon = css`
  background-color: var(--c-blue-400);
  border-radius: 50%;
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
  outline: 1px solid var(--plus-icon-outline, var(--c-nd-50-opaque));
  margin-left: -7px;
`;

type Props = {
  balance: number;
  variant?: 'vertical' | 'horizontal';
  color?: 'tertiary';
};
export const GemsBalance = ({ balance, variant, color }: Props) => {
  const track = useEvents();
  const openDialog = useOpenDialog();
  const onClick = () => {
    track('Open Buy Gems Dialog', {
      context: OpenBuyGemsDialog_Context.GEMS_BALANCE,
    });
    openDialog(DialogKey.buyGems, 'true');
  };

  if (color === 'tertiary') {
    return (
      <Button
        style={{ '--plus-icon-outline': 'var(--c-black)' } as CSSProperties}
        size="small"
        color="tertiary"
        onClick={onClick}
        endIcon={
          <Horizontal gap={0.5}>
            <GemIcon size={1.5} />
            {balance <= 0 && (
              <Horizontal className={plusIcon} gap={0} center>
                <Plus fill="white" />
              </Horizontal>
            )}
          </Horizontal>
        }
      >
        <AnimatedNumber value={balance} disableAnimationOnFirstRender />
      </Button>
    );
  }

  if (variant === 'vertical') {
    return (
      <ButtonBase onClick={onClick} className={classNames(root, 'vertical')}>
        <Vertical center gap={0.5}>
          <Horizontal gap={0.5}>
            <GemIcon size={1.5} />
            {balance <= 0 && (
              <Horizontal className={plusIcon} gap={0} center>
                <Plus fill="white" />
              </Horizontal>
            )}
          </Horizontal>
          <LabelM bold>
            <AnimatedNumber value={balance} disableAnimationOnFirstRender />
          </LabelM>
        </Vertical>
      </ButtonBase>
    );
  }

  return (
    <ButtonBase onClick={onClick} className={root}>
      <Horizontal gap={0.5}>
        <LabelM bold>
          <AnimatedNumber value={balance} disableAnimationOnFirstRender />
        </LabelM>
        <GemIcon size={1.5} />
        {balance <= 0 && (
          <Horizontal className={plusIcon} gap={0} center>
            <Plus fill="white" />
          </Horizontal>
        )}
      </Horizontal>
    </ButtonBase>
  );
};
